import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Phone, Mail } from 'lucide-react';
import { AuthProvider } from './contexts/AuthContext';
import { PartnerAuthProvider } from './contexts/PartnerAuthContext';
import { LanguageProvider } from './i18n/LanguageContext';
import { TrackingProvider } from './contexts/TrackingContext';
import Header from './components/Header';
import Sidebar, { SIDEBAR_WIDTH, LS_KEY } from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedPartnerRoute from './components/ProtectedPartnerRoute';
import Home from './pages/Home';
import HomeV1 from './pages/HomeV1';
import Login from './pages/Login';
import Profile from './pages/Profile';
import TestProfile from './pages/TestProfile';
import Sports from './pages/Sports';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';

import UserManagement from './pages/UserManagement';
import DataManagement from './pages/DataManagement';
import Countries from './pages/Countries';
import Leagues from './pages/Leagues';
import Clubs from './pages/Clubs';
import Seasons from './pages/Seasons';
import Competitions from './pages/Competitions';
import Matchdays from './pages/Matchdays';
import Matches from './pages/Matches';

import Tracking from './pages/Tracking';
import LiveTrackingPage from './pages/LiveTrackingPage';
import Partners from './pages/Partners';
import CustomPartners from './pages/CustomPartners';
import PartnerAccess from './pages/PartnerAccess';
import Transportation from './pages/Transportation';
import LocalMatches from './pages/LocalMatchesNew';
import LocalMatchdays from './pages/LocalMatchdaysNew';
import Travelers from './pages/Travelers';
import SportsManagement from './pages/SportsManagement';
import MatchdayPlanning from './pages/MatchdayPlanning';
import DemoHeimspiel from './pages/DemoHeimspiel';
import DemoAuswaertsspiel from './pages/DemoAuswaertsspiel';
import UpcomingMatches from './pages/UpcomingMatches';

// Partner pages
import PartnerLogin from './pages/PartnerLogin';
import PartnerRegister from './pages/PartnerRegister';
import PartnerDashboard from './pages/PartnerDashboard';
import PartnerMatchdayPlannings from './pages/PartnerMatchdayPlannings';

// Settings pages
import TomTomSetup from './pages/TomTomSetup';
import EmissionFactors from './pages/EmissionFactors';
import Sustainability from './pages/Sustainability';


/**
 * Main application component with routing and authentication context
 */
function App() {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    return localStorage.getItem(LS_KEY) === 'true';
  });

  const toggleSidebar = () => setSidebarOpen(prev => !prev);
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <LanguageProvider>
      <AuthProvider>
        <PartnerAuthProvider>
          <TrackingProvider>
          <Router>
            <div style={appStyle}>
              <Header onToggleSidebar={toggleSidebar} />
              <div style={bodyContainerStyle}>
                <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
                <main style={{
                  ...mainStyle,
                  transition: 'margin-left 0.3s ease',
                }}>
                <Routes>
                {/* Public routes */}
                <Route path="/" element={<Home />} />
                <Route path="/home-v1" element={<HomeV1 />} />
                <Route path="/login" element={<Login />} />

                {/* Partner routes */}
                <Route path="/partner/login" element={<PartnerLogin />} />
                <Route path="/partner/register" element={<PartnerRegister />} />
                <Route
                  path="/partner/dashboard"
                  element={
                    <ProtectedPartnerRoute>
                      <PartnerDashboard />
                    </ProtectedPartnerRoute>
                  }
                />
                <Route
                  path="/partner/matchday-planning"
                  element={
                    <ProtectedPartnerRoute>
                      <PartnerMatchdayPlannings />
                    </ProtectedPartnerRoute>
                  }
                />
                <Route
                  path="/partner/matchday-planning/:planId"
                  element={
                    <ProtectedPartnerRoute>
                      <MatchdayPlanning />
                    </ProtectedPartnerRoute>
                  }
                />

                {/* Demo & Tour routes */}
                <Route path="/demo" element={<Login />} />
                <Route path="/tour" element={<Login />} />
                <Route path="/demo/heimspiel" element={<DemoHeimspiel />} />
                <Route path="/demo/auswaertsspiel" element={<DemoAuswaertsspiel />} />
              
              {/* Protected routes */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/test-profile"
                element={
                  <ProtectedRoute>
                    <TestProfile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/sports"
                element={
                  <ProtectedRoute>
                    <Sports />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/upcoming-matches"
                element={<UpcomingMatches />}
              />

              <Route
                path="/sports-management"
                element={
                  <ProtectedRoute requiredRole="Admin">
                    <SportsManagement />
                  </ProtectedRoute>
                }
              />



              <Route
                path="/countries"
                element={
                  <ProtectedRoute>
                    <Countries />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/leagues"
                element={
                  <ProtectedRoute>
                    <Leagues />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clubs"
                element={
                  <ProtectedRoute>
                    <Clubs />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/seasons"
                element={
                  <ProtectedRoute>
                    <Seasons />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/competitions"
                element={
                  <ProtectedRoute>
                    <Competitions />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/matchdays"
                element={
                  <ProtectedRoute>
                    <Matchdays />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/matches"
                element={
                  <ProtectedRoute>
                    <Matches />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/partners"
                element={
                  <ProtectedRoute>
                    <Partners />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/custom-partners"
                element={
                  <ProtectedRoute>
                    <CustomPartners />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/partner-access"
                element={
                  <ProtectedRoute>
                    <PartnerAccess />
                  </ProtectedRoute>
                }
              />



              <Route
                path="/matchday-planning"
                element={
                  <ProtectedRoute>
                    <MatchdayPlanning />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/matchday-planning/:planId"
                element={
                  <ProtectedRoute>
                    <MatchdayPlanning />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/tracking"
                element={
                  <ProtectedRoute>
                    <Tracking />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/local-matches"
                element={
                  <ProtectedRoute>
                    <LocalMatches />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/local-matchdays"
                element={
                  <ProtectedRoute>
                    <LocalMatchdays />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/travelers"
                element={
                  <ProtectedRoute>
                    <Travelers />
                  </ProtectedRoute>
                }
              />

              {/* Legacy routes for backward compatibility */}
              <Route
                path="/drivers"
                element={
                  <ProtectedRoute>
                    <Travelers />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/players"
                element={
                  <ProtectedRoute>
                    <Travelers />
                  </ProtectedRoute>
                }
              />



              <Route
                path="/user-management"
                element={
                  <ProtectedRoute requiredRole="Admin">
                    <UserManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/data-management"
                element={
                  <ProtectedRoute requiredRole="Admin">
                    <DataManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/transportation"
                element={
                  <ProtectedRoute>
                    <Transportation />
                  </ProtectedRoute>
                }
              />

              {/* Settings Pages */}
              <Route
                path="/settings/tomtom"
                element={
                  <ProtectedRoute requiredRole="Admin">
                    <TomTomSetup />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/emission-factors"
                element={
                  <ProtectedRoute requiredRole="Admin">
                    <EmissionFactors />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/sustainability"
                element={
                  <ProtectedRoute requiredRole="Admin">
                    <Sustainability />
                  </ProtectedRoute>
                }
              />

              {/* Legal Pages */}
              {/* Public live tracking (no auth, no header) */}
              <Route path="/live/:token" element={<LiveTrackingPage />} />

              <Route path="/impressum" element={<Impressum />} />
              <Route path="/datenschutz" element={<Datenschutz />} />

              {/* Catch-all route for 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
              </div>
          <Footer />
        </div>
      </Router>
      </TrackingProvider>
      </PartnerAuthProvider>
    </AuthProvider>
    </LanguageProvider>
  );
}

/**
 * 404 Not Found component
 */
const NotFound: React.FC = () => {
  return (
    <div style={notFoundStyle}>
      <div style={notFoundContentStyle}>
        <h1 style={notFoundTitleStyle}>404</h1>
        <h2 style={notFoundSubtitleStyle}>Page Not Found</h2>
        <p style={notFoundMessageStyle}>
          The page you're looking for doesn't exist.
        </p>
        <a href="/" style={notFoundLinkStyle}>
          Go back to Home
        </a>
      </div>
    </div>
  );
};

/**
 * Footer component
 */
const Footer: React.FC = () => {
  return (
    <footer style={footerStyle}>
      <div style={footerContentStyle}>
        <div style={footerSectionStyle}>
          <h4 style={footerTitleStyle}>VoyagePlanner GmbH</h4>
          <p style={footerTextStyle}>
            Ihre Plattform für professionelle Reiseplanung
          </p>
          <p style={footerTextStyle}>
            Geschäftsführer: Sven Mahn<br />
            Saseler Damm 43-45<br />
            22395 Hamburg
          </p>
        </div>

        <div style={footerSectionStyle}>
          <h4 style={footerTitleStyle}>Kontakt</h4>
          <ul style={footerListStyle}>
            <li style={footerListItemStyle}>
              <a href="tel:+4940226348012" style={footerLinkStyle}>
                <Phone size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> 040 226 348 012
              </a>
            </li>
            <li style={footerListItemStyle}>
              <a href="mailto:kontakt@voyageplanner.app" style={footerLinkStyle}>
                <Mail size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> kontakt@voyageplanner.app
              </a>
            </li>
          </ul>
        </div>

        <div style={footerSectionStyle}>
          <h4 style={footerTitleStyle}>Rechtliches</h4>
          <ul style={footerListStyle}>
            <li style={footerListItemStyle}>
              <Link to="/impressum" style={footerLinkStyle}>
                Impressum
              </Link>
            </li>
            <li style={footerListItemStyle}>
              <Link to="/datenschutz" style={footerLinkStyle}>
                Datenschutz
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div style={footerBottomStyle}>
        <p style={footerCopyrightStyle}>
          © 2025 VoyagePlanner GmbH | HRB 118354 Amtsgericht Hamburg
          <span style={footerVersionStyle}>v{__APP_VERSION__}</span>
        </p>
      </div>
    </footer>
  );
};

// Styles
const appStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
};

const bodyContainerStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 'calc(100vh - 49px)',
};

const notFoundStyle: React.CSSProperties = {
  minHeight: 'calc(100vh - 200px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f8f9fa',
  padding: '2rem',
};

const notFoundContentStyle: React.CSSProperties = {
  textAlign: 'center',
  backgroundColor: 'white',
  padding: '3rem',
  borderRadius: '8px',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  maxWidth: '500px',
  width: '100%',
};

const notFoundTitleStyle: React.CSSProperties = {
  fontSize: '6rem',
  fontWeight: 'bold',
  color: '#e74c3c',
  marginBottom: '1rem',
  lineHeight: 1,
};

const notFoundSubtitleStyle: React.CSSProperties = {
  fontSize: '2rem',
  color: '#2c3e50',
  marginBottom: '1rem',
};

const notFoundMessageStyle: React.CSSProperties = {
  color: '#7f8c8d',
  fontSize: '1.1rem',
  marginBottom: '2rem',
  lineHeight: '1.5',
};

const notFoundLinkStyle: React.CSSProperties = {
  backgroundColor: '#3498db',
  color: 'white',
  padding: '1rem 2rem',
  borderRadius: '8px',
  textDecoration: 'none',
  fontSize: '1.1rem',
  fontWeight: 'bold',
  display: 'inline-block',
  transition: 'background-color 0.2s',
};

const footerStyle: React.CSSProperties = {
  backgroundColor: '#2c3e50',
  color: 'white',
  marginTop: 'auto',
};

const footerContentStyle: React.CSSProperties = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '2rem',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '2rem',
};

const footerSectionStyle: React.CSSProperties = {
  marginBottom: '1rem',
};

const footerTitleStyle: React.CSSProperties = {
  fontSize: '1.2rem',
  marginBottom: '1rem',
  color: '#ecf0f1',
};

const footerTextStyle: React.CSSProperties = {
  color: '#bdc3c7',
  lineHeight: '1.5',
  fontSize: '0.9rem',
};

const footerListStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const footerListItemStyle: React.CSSProperties = {
  color: '#bdc3c7',
  fontSize: '0.9rem',
  marginBottom: '0.5rem',
};

const footerLinkStyle: React.CSSProperties = {
  color: '#bdc3c7',
  textDecoration: 'none',
  transition: 'color 0.3s ease',
};

const footerBottomStyle: React.CSSProperties = {
  borderTop: '1px solid #34495e',
  padding: '1rem 2rem',
  textAlign: 'center',
};

const footerCopyrightStyle: React.CSSProperties = {
  color: '#95a5a6',
  fontSize: '0.9rem',
  margin: 0,
};

const footerVersionStyle: React.CSSProperties = {
  marginLeft: '1rem',
  opacity: 0.7,
  fontSize: '0.8rem',
};

// Add CSS for interactive elements
const style = document.createElement('style');
style.textContent = `
  footer a:hover {
    color: #3498db !important;
  }

  .not-found-link:hover {
    background-color: #2980b9;
  }

  @media (max-width: 768px) {
    .footer-content {
      grid-template-columns: 1fr;
      text-align: center;
    }

    .not-found-title {
      font-size: 4rem !important;
    }

    .not-found-subtitle {
      font-size: 1.5rem !important;
    }
  }
`;
document.head.appendChild(style);

export default App;
